import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '../../core/api.service'; // Adjust path as needed
import Swal from 'sweetalert2';

interface Question {
  question_id: string;
  question_text: string;
  question_type: string;
  options: string[];
  is_required: boolean;
  _id: string;
}

interface AssignedCustomer {
  customer_id: string;
  customer_name: string;
  assigned_date: string;
  survey_status: string;
  assigned_to_employee: string;
  assigned_to_employee_name: string;
  _id: string;
}

interface Answer {
  question_id: string;
  star_rating?: number;
  selected_option?: string;
  text_answer?: string;
  _id: string;
}

interface SurveyResponse {
  customer_id: string;
  customer_name: string;
  answers: Answer[];
  remarks: string;
  conducted_date: string;
  conducted_by: string;
  conducted_by_employee_name: string;
  _id: string;
}

interface SurveyDetails {
  survey_id?: string;
  survey_number?: string;
  survey_title?: string;
  survey_description?: string;
  questions?: Question[];
  assigned_customers?: AssignedCustomer[];
  created_by?: {
    employee_id?: string;
    employee_name?: string;
  };
  status?: string;
  created_date_time?: string;
  responses?: SurveyResponse[];
  total_assigned?: number;
  total_completed?: number;
  completion_percentage?: string;
  updated_at?: string;
  pending_count?: number;
}

@Component({
  selector: 'app-customer-survey-detail',
  templateUrl: './customer-survey-detail.component.html',
  styleUrls: ['./customer-survey-detail.component.css']
})
export class CustomerSurveyDetailComponent implements OnInit {
  
  Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
  });

  // Survey ID from route
  surveyId: string = '';
  
  // Survey data
  surveyDetails: SurveyDetails | null = null;
  
  // UI state
  loading: boolean = false;
  errorMessage: string = '';
  successMessage: string = '';
  activeTab: 'overview' | 'questions' | 'customers' | 'responses' = 'overview';
  
  // Current user info
  currentUser = {
    employee_id: '',
    employee_name: '',
    role: ''
  };

  // Debug mode
  debugMode: boolean = true;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService
  ) {}

  ngOnInit(): void {
    this.loadUserInfo();
    this.getSurveyIdFromRoute();
  }

  loadUserInfo(): void {
    this.currentUser = {
      employee_id: localStorage.getItem('employee_id') || 
                   localStorage.getItem('admin_id') || 
                   localStorage.getItem('user_id') || 
                   '',
      employee_name: localStorage.getItem('employee_name') || 
                     localStorage.getItem('EmployeeName') || 
                     localStorage.getItem('user_name') || 
                     '',
      role: localStorage.getItem('user_type') || 
            localStorage.getItem('userType') || 
            localStorage.getItem('userTypecon') ||
            localStorage.getItem('role') || 
            localStorage.getItem('user_role') || 
            localStorage.getItem('UserType') || 
            ''
    };

    if (this.debugMode) {
      console.log('🔍 Current User:', this.currentUser);
    }
  }

  getSurveyIdFromRoute(): void {
    // First try query params (most likely from your listing)
    this.route.queryParams.subscribe(params => {
      this.surveyId = params['surveyId'];
      
      if (this.debugMode) {
        console.log('📋 Query Parameters:', params);
        console.log('🔍 Survey ID from query params:', this.surveyId);
      }
      
      if (this.surveyId) {
        this.loadSurveyDetails();
      } else {
        // If no query params, try route params
        this.route.params.subscribe(routeParams => {
          this.surveyId = routeParams['surveyId'] || routeParams['id'];
          
          if (this.debugMode) {
            console.log('📋 Route Parameters:', routeParams);
            console.log('🔍 Survey ID from route params:', this.surveyId);
          }
          
          if (this.surveyId) {
            this.loadSurveyDetails();
          } else {
            this.errorMessage = 'No survey ID found. Please select a survey from the list.';
            console.error('❌ No survey ID found in route or query params');
          }
        });
      }
    });
  }

  loadSurveyDetails(): void {
    if (!this.surveyId || this.surveyId.trim() === '') {
      this.errorMessage = 'Invalid survey ID';
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.successMessage = '';

    if (this.debugMode) {
      console.log('📞 Loading survey details for ID:', this.surveyId);
    }

    // Try primary endpoint
    this.apiService.get(`FactoryCustomerSurvey/detail/${this.surveyId}`).subscribe({
      next: (response: any) => {
        console.log('✅ Survey details response:', response);
        
        if (response.success && response.data) {
          this.surveyDetails = response.data;
          this.loading = false;
          
          if (this.debugMode && this.surveyDetails) {
            console.log('📊 Survey data loaded:', {
              title: this.surveyDetails.survey_title,
              assigned: this.surveyDetails.total_assigned,
              completed: this.surveyDetails.total_completed,
              status: this.surveyDetails.status
            });
          }
        } else {
          this.errorMessage = response.message || 'Survey data not found';
          this.loading = false;
        }
      },
      error: (error: any) => {
        console.error('❌ Error loading survey details:', error);
        
        // Try alternative endpoint
        this.apiService.get(`customer_survey/detail/${this.surveyId}`).subscribe({
          next: (response: any) => {
            console.log('✅ Survey details (alternative):', response);
            
            if (response.success && response.data) {
              this.surveyDetails = response.data;
              this.loading = false;
            } else {
              this.errorMessage = response.message || 'Survey data not found';
              this.loading = false;
            }
          },
          error: (err: any) => {
            console.error('❌ Error loading survey details (alternative):', err);
            this.errorMessage = 'Failed to load survey details. Please check the survey ID.';
            this.loading = false;
            
            this.Toast.fire({
              icon: 'error',
              title: 'Failed to load survey details'
            });
          }
        });
      }
    });
  }

  // Helper methods for template
  getQuestionText(questionId: string): string {
    if (!this.surveyDetails?.questions) return '';
    const question = this.surveyDetails.questions.find(q => q.question_id === questionId);
    return question ? question.question_text : `Question ${questionId}`;
  }

  getResponseForCustomer(customerId: string): SurveyResponse | undefined {
    if (!this.surveyDetails?.responses) return undefined;
    return this.surveyDetails.responses.find(r => r.customer_id === customerId);
  }

  hasCustomerResponded(customerId: string): boolean {
    return !!this.getResponseForCustomer(customerId);
  }

  getAnswerForQuestion(response: SurveyResponse, questionId: string): Answer | undefined {
    if (!response?.answers) return undefined;
    return response.answers.find(a => a.question_id === questionId);
  }

  formatDate(dateString: string | undefined): string {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  }

  formatDateShort(dateString: string | undefined): string {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  }

  getCompletionPercentage(): number {
    if (!this.surveyDetails) return 0;
    
    // Try to parse from API if available
    if (this.surveyDetails.completion_percentage) {
      const parsed = parseFloat(this.surveyDetails.completion_percentage);
      if (!isNaN(parsed)) return Math.round(parsed);
    }
    
    // Calculate if not provided
    const total = this.surveyDetails.total_assigned || 0;
    const completed = this.surveyDetails.total_completed || 0;
    
    if (total === 0) return 0;
    return Math.round((completed / total) * 100);
  }

  getAssignedEmployees(): string[] {
    if (!this.surveyDetails?.assigned_customers) return [];
    
    const uniqueEmployees = [...new Set(
      this.surveyDetails.assigned_customers
        .filter(customer => customer.assigned_to_employee_name && customer.assigned_to_employee_name !== 'Unknown Employee')
        .map(customer => customer.assigned_to_employee_name)
    )];
    
    return uniqueEmployees;
  }

  getStatusClass(status: string | undefined): string {
    if (!status) return 'badge-secondary';
    
    switch (status.toLowerCase()) {
      case 'pending': return 'badge-warning';
      case 'in progress': return 'badge-info';
      case 'completed': 
      case 'conducted': return 'badge-success';
      default: return 'badge-secondary';
    }
  }

  getCustomerStatusClass(status: string | undefined): string {
    if (!status) return 'badge-secondary';
    
    switch (status.toLowerCase()) {
      case 'pending': return 'badge-warning';
      case 'conducted': 
      case 'completed': return 'badge-success';
      default: return 'badge-secondary';
    }
  }

  // Format question type for display
  formatQuestionType(type: string): string {
    if (!type) return 'Unknown';
    
    const typeMap: { [key: string]: string } = {
      'star_rating': 'Star Rating',
      'multiple_choice': 'Multiple Choice',
      'text': 'Text Response',
      'yes_no': 'Yes/No',
      'checkbox': 'Checkbox',
      'dropdown': 'Dropdown'
    };
    
    return typeMap[type] || type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  // Get employee display name
  getEmployeeDisplayName(customer: AssignedCustomer): string {
    return customer.assigned_to_employee_name || 
           customer.assigned_to_employee || 
           'Unassigned';
  }

  // Navigation methods
  goBack(): void {
    this.router.navigate(['admin/customer-survey-listing']);
  }

  exportToExcel(): void {
    if (!this.surveyId) {
      this.Toast.fire({
        icon: 'warning',
        title: 'Survey ID is required'
      });
      return;
    }

    // Open export in new tab - the API service will handle the full URL
    window.open(`FactoryCustomerSurvey/export/${this.surveyId}`, '_blank');
    
    this.Toast.fire({
      icon: 'info',
      title: 'Generating Excel export...'
    });
  }

  // Submit response for a customer
  submitResponseForCustomer(customer: AssignedCustomer): void {
    if (!this.surveyDetails) return;
    
    this.router.navigate(['admin/customer-survey-edit'], {
      queryParams: {
        surveyId: this.surveyDetails.survey_id,
        customerId: customer.customer_id,
        customerName: customer.customer_name,
        mode: 'response'
      }
    });
  }

  // View response for a customer - FIXED: Added missing method
  viewResponse(customer: AssignedCustomer): void {
    if (!this.surveyDetails) return;
    
    const response = this.getResponseForCustomer(customer.customer_id);
    
    if (!response) {
      this.Toast.fire({
        icon: 'warning',
        title: 'No response found for this customer'
      });
      return;
    }
    
    // Navigate to the same edit page but in view mode
    this.router.navigate(['admin/customer-survey-edit'], {
      queryParams: {
        surveyId: this.surveyDetails.survey_id,
        customerId: customer.customer_id,
        customerName: customer.customer_name,
        mode: 'view'
      }
    });
  }

  // Check if current user is HR/Admin
  isHROrAdmin(): boolean {
    if (!this.currentUser.role) return false;
    const role = this.currentUser.role.toLowerCase().trim();
    return role.includes('hr') || role.includes('admin');
  }

  // Check if current user can submit response
  canSubmitResponse(): boolean {
    // Regular employees can submit, HR/Admins can view only
    return !this.isHROrAdmin();
  }

  // Set active tab
  setActiveTab(tab: 'overview' | 'questions' | 'customers' | 'responses'): void {
    this.activeTab = tab;
  }

  // Reload survey data
  reloadSurvey(): void {
    this.loadSurveyDetails();
  }

  // Get question type icon
  getQuestionTypeIcon(type: string): string {
    const iconMap: { [key: string]: string } = {
      'star_rating': 'fas fa-star',
      'multiple_choice': 'fas fa-list-ul',
      'text': 'fas fa-align-left',
      'yes_no': 'fas fa-check-circle',
      'checkbox': 'fas fa-check-square'
    };
    
    return iconMap[type] || 'fas fa-question-circle';
  }

  // Safe array access helper
  safeArrayLength(arr: any[] | undefined): number {
    return arr?.length || 0;
  }
}